/* @jsx h */
import { defineComponent, h, onMounted, ref } from "vue";
import { Loading, useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import { Avatar } from "../../shared/Avatar";
import { localDate, shiftDate } from "../../shared/dates";

type Request = {
  id: number;
  staff_id: number;
  name: string;
  color: string | null;
  start_date: string;
  end_date: string;
  reason: string | null;
  status: "pending" | "approved" | "declined" | "cancelled";
};

const day = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });

// Time off the team asked for (and entered by managers): approve or decline.
export const TimeOffRequests = defineComponent({
  name: "TimeOffRequests",
  setup() {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();
    const list = ref<Request[]>([]);
    const loaded = ref(false);
    const members = ref<{ id: number; name: string }[]>([]);
    const adding = ref(false);
    const who = ref<number | null>(null);
    const start = ref(localDate());
    const end = ref(localDate());

    const load = async () => {
      list.value = (await trpc.rota.timeOff.list.query({ from: shiftDate(localDate(), -30), to: shiftDate(localDate(), 365) })) as Request[];
      loaded.value = true;
    };
    onMounted(async () => {
      await load();
      members.value = (await trpc.rota.team.list.query()).members.filter((m) => m.active).map((m) => ({ id: m.id, name: m.name }));
    });

    const decide = async (r: Request, status: "approved" | "declined" | "pending") => {
      list.value = list.value.map((x) => (x.id === r.id ? { ...x, status } : x));
      try {
        await trpc.rota.timeOff.decide.mutate({ id: r.id, status });
        await load();
      } catch {
        toast.showToast(t("workday.common.save_failed"), { type: "error" });
      }
    };

    const add = async () => {
      if (!who.value) return;
      await trpc.rota.timeOff.create.mutate({ staff_id: who.value, start_date: start.value, end_date: end.value < start.value ? start.value : end.value });
      adding.value = false;
      await load();
    };

    return () => (
      <div class="rounded-2xl border border-base-300 bg-base-100 p-4">
        <div class="mb-3 flex items-center justify-between">
          <span class="font-semibold">{t("workday.requests.time_off")}</span>
          <button class="btn btn-ghost btn-sm border-base-300" onClick={() => (adding.value = !adding.value)}>
            <span class="material-symbols-outlined text-base">add</span>
            {t("workday.requests.add_time_off")}
          </button>
        </div>
        {adding.value && (
          <div class="mb-3 flex flex-wrap items-end gap-2 rounded-xl bg-base-200 p-3">
            <select class="select select-bordered select-sm" value={who.value ?? ""} onChange={(e: Event) => (who.value = Number((e.target as HTMLSelectElement).value) || null)}>
              <option value="">{t("workday.requests.pick_person")}</option>
              {members.value.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
            <input type="date" class="input input-bordered input-sm" v-model={start.value} />
            <input type="date" class="input input-bordered input-sm" v-model={end.value} />
            <button class="btn btn-primary btn-sm" disabled={!who.value} onClick={add}>
              {t("workday.common.save")}
            </button>
          </div>
        )}
        {!loaded.value ? (
          <div class="flex justify-center py-6">
            <Loading />
          </div>
        ) : list.value.length === 0 ? (
          <p class="py-6 text-center text-sm text-base-content/50">{t("workday.requests.no_time_off")}</p>
        ) : (
          <ul class="divide-y divide-base-200">
            {list.value.map((r) => (
              <li key={r.id} class="flex flex-wrap items-center gap-3 py-2.5">
                <Avatar name={r.name} id={r.staff_id} color={r.color} size="sm" />
                <div class="min-w-0 flex-1">
                  <div class="text-sm font-medium">{r.name}</div>
                  <div class="text-xs text-base-content/60">
                    {r.start_date === r.end_date ? day(r.start_date) : `${day(r.start_date)} – ${day(r.end_date)}`}
                    {r.reason && ` · ${r.reason}`}
                  </div>
                </div>
                {r.status === "pending" ? (
                  <div class="flex gap-1">
                    <button class="btn btn-ghost btn-sm" onClick={() => decide(r, "declined")}>
                      {t("workday.requests.decline")}
                    </button>
                    <button class="btn btn-success btn-sm" onClick={() => decide(r, "approved")}>
                      {t("workday.requests.approve")}
                    </button>
                  </div>
                ) : (
                  <button class={["badge badge-sm cursor-pointer", r.status === "approved" ? "badge-success" : "badge-error badge-outline"].join(" ")} title={t("workday.requests.undo")} onClick={() => decide(r, "pending")}>
                    {t(`teamlink.page.status_${r.status}`)}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  },
});
